import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage, useIntl } from 'dibs-react-intl';
import ChairIcon from 'dibs-icons/exports/legacy/ChairOutlined.ts';

import { getOrderStatusLabel } from '../getOrderStatusLabel.tsx';

import { type GetHelpZendeskSellerOrderTile_viewer$key } from './__generated__/GetHelpZendeskSellerOrderTile_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment GetHelpZendeskSellerOrderTile_viewer on Viewer {
        transaction(transactionId: $transactionId) @include(if: $hasOrderId) {
            serviceId
            item {
                firstPhotoWebPath(size: small)
                title
            }
            ...getOrderStatusLabel_transaction
        }
    }
`;

const GetHelpZendeskSellerOrderTile: FC<{ viewer: GetHelpZendeskSellerOrderTile_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const intl = useIntl();
    const viewer = useFragment(viewerFragment, viewerRef);
    const { transaction } = viewer;
    const { item, serviceId: transactionId } = transaction || {};
    const { title, firstPhotoWebPath } = item || {};

    const statusLabel = getOrderStatusLabel({ transaction, isSeller: true, intl });

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.flexRow,
                dibsCss.itemsCenter,
                dibsCss.gapXSmall
            )}
        >
            <div className={classnames(dibsCss.hXxlarge, dibsCss.wXxlarge, dibsCss.minWXxlarge)}>
                {firstPhotoWebPath ? (
                    <img
                        alt={title || ''}
                        src={firstPhotoWebPath}
                        className={classnames(dibsCss.hFull, dibsCss.wFull, dibsCss.objectCover)}
                    />
                ) : (
                    <ChairIcon className={classnames(dibsCss.hXxlarge, dibsCss.wXxlarge)} />
                )}
            </div>
            <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapYXsmall)}>
                <div className={dibsCss.sassyFontHeaderMedium}>{title}</div>
                {transactionId && (
                    <div className={dibsCss.sassyFontBodySmall}>
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskSellerOrder.orderNumber"
                            defaultMessage="Order {transactionId}"
                            values={{ transactionId }}
                        />
                        {statusLabel && (
                            <>
                                {' | '}
                                <span className={dibsCss.sassyFontBodySmallHeavy}>
                                    {statusLabel}
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export { GetHelpZendeskSellerOrderTile };
